import { createBrowserRouter, Navigate } from 'react-router';
import { ROUTES } from '@/constants';
import { RootLayout } from './root';
import { RequireAnonymous, RequireAuth } from './guard';
import { RouteError } from './error';
import { SecurityPage } from './security/page';
import { AdminDashboardPage } from './dashboard/page';
import { AuditPage } from './audit/page';
import { LoginPage } from './login/page';
import { CustomersPage } from './customers/page';
import { CustomerDetailPage } from './customers/detail/page';

/**
 * The route tree.
 *
 * Guards are layout routes rather than per-page checks: a new page added under
 * RequireAuth is protected because of where it sits, not because someone
 * remembered to wrap it. Same reasoning as the server composing its auth chain
 * once per surface.
 *
 * The literal `/customers/new` is declared before the dynamic customer route so
 * 'new' is matched as itself rather than swallowed as an id - a static segment
 * losing to a dynamic one produces a 404 that looks like a data problem.
 *
 * It renders the same `CustomersPage` as `/customers` - creating a customer is
 * a dialog over the list now, not a page of its own, and this route exists so
 * that dialog is still reachable by a direct link rather than only by clicking
 * the button. See CustomerTable for what actually decides whether the dialog
 * starts open.
 */
export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      {
        element: <RequireAnonymous />,
        children: [{ path: ROUTES.login, element: <LoginPage /> }],
      },
      {
        element: <RequireAuth />,
        children: [
          {
            element: <RootLayout />,
            children: [
              // There is no dashboard: for staff, the customer list IS the
              // landing page. A dashboard of invented metrics would be worse
              // than none.
              { path: ROUTES.home, element: <Navigate to={ROUTES.customers} replace /> },
              { path: ROUTES.dashboard, element: <AdminDashboardPage /> },
              { path: ROUTES.customers, element: <CustomersPage /> },
              { path: ROUTES.audit, element: <AuditPage /> },
              { path: ROUTES.security, element: <SecurityPage /> },
              { path: ROUTES.newCustomer, element: <CustomersPage /> },
              // Built from the same helper that constructs links to it, so the
              // pattern and its call sites cannot drift apart.
              {
                path: ROUTES.customerDetail(':organizationId'),
                element: <CustomerDetailPage />,
              },
            ],
          },
        ],
      },
      { path: '*', element: <RouteError /> },
    ],
  },
]);
